import { Platform, type TextStyle } from 'react-native';
import { colors } from './colors';
import { typographyTokens as tokens } from './typographyTokens';

/** Compatibility family names. Brand/display copy opts into explicit display tokens;
 * legacy `fonts.display` also appears in buttons and small headings, so it is UI.
 */
export const fonts = {
    display: Platform.OS === 'web' ? 'var(--ag-font-ui)' : 'NunitoSans-Bold',
    text: Platform.OS === 'web' ? 'var(--ag-font-ui)' : 'NunitoSans-Regular',
    brand: Platform.OS === 'web' ? 'var(--ag-font-brand)' : 'Lora-Medium',
    heading: Platform.OS === 'web' ? 'var(--ag-font-ui)' : 'NunitoSans-SemiBold',
};

const web = Platform.OS === 'web';

/** Preserve the existing native scale until its separate responsive migration. */
const legacyTypography = {
    h1: { fontFamily: fonts.display, fontSize: 42, fontWeight: undefined, lineHeight: 48, letterSpacing: -2, color: colors.obsidian },
    h1Hero: { fontFamily: fonts.display, fontSize: 34, fontWeight: undefined, lineHeight: 40, letterSpacing: -1.5, color: colors.obsidian },
    h1Page: { fontFamily: fonts.display, fontSize: 42, fontWeight: undefined, lineHeight: 48, letterSpacing: -2, color: colors.obsidian },
    h2: { fontFamily: fonts.display, fontSize: 28, fontWeight: undefined, lineHeight: 34, letterSpacing: -1.5, color: colors.obsidian },
    sectionTitle: { fontFamily: fonts.display, fontSize: 26, fontWeight: undefined, lineHeight: 32, letterSpacing: -1.2, color: colors.obsidian },
    communityHeader: { fontFamily: fonts.heading, fontSize: 27, fontWeight: undefined, lineHeight: 34, letterSpacing: -0.9, color: colors.obsidian },
    h3: { fontFamily: fonts.heading, fontSize: 28, fontWeight: undefined, lineHeight: 34, letterSpacing: -1, color: colors.obsidian },
    h4: { fontFamily: fonts.heading, fontSize: 22, fontWeight: undefined, lineHeight: 30, letterSpacing: -0.5, color: colors.obsidian },
    h5: { fontFamily: fonts.heading, fontSize: 18, fontWeight: undefined, lineHeight: 26, letterSpacing: -0.3, color: colors.obsidian },
    subtitle: { fontFamily: fonts.text, fontSize: 18, fontWeight: undefined, lineHeight: 28, letterSpacing: 0, color: colors.obsidian, opacity: 0.6 },
    bodyLarge: { fontFamily: fonts.text, fontSize: 18, fontWeight: undefined, lineHeight: 28, letterSpacing: 0, color: colors.textSecondary },
    body: { fontFamily: fonts.text, fontSize: 16, fontWeight: undefined, lineHeight: 24, letterSpacing: 0, color: colors.textSecondary },
    bodySmall: { fontFamily: fonts.text, fontSize: 18, fontWeight: undefined, lineHeight: 28, letterSpacing: 0, color: colors.gray },
    caption: { fontFamily: fonts.text, fontSize: 12, fontWeight: undefined, lineHeight: 16, letterSpacing: 0, color: colors.gray },
    micro: { fontFamily: fonts.text, fontSize: 14, fontWeight: undefined, lineHeight: 18, letterSpacing: 0, color: colors.gray },
    overline: { fontFamily: fonts.heading, fontSize: 14, fontWeight: undefined, lineHeight: 20, letterSpacing: 0, textTransform: 'none' as TextStyle['textTransform'], color: colors.solar },
    button: { fontFamily: fonts.display, fontSize: 16, fontWeight: undefined, lineHeight: 24, letterSpacing: 0, textTransform: 'none' as TextStyle['textTransform'] },
};

/** Existing native layouts retain their scale; Web aliases use explicit UI/display roles. */
export const typography = {
    ...tokens,
    ...legacyTypography,
    ...(web ? {
        h1: { ...tokens.uiH1, color: colors.obsidian },
        h1Hero: { ...tokens.displayH1, color: colors.obsidian },
        h1Page: { ...tokens.uiH1, color: colors.obsidian },
        h2: { ...tokens.uiH2, color: colors.obsidian },
        sectionTitle: { ...tokens.uiH2, color: colors.obsidian },
        communityHeader: { ...tokens.uiH3, color: colors.obsidian },
        h3: { ...tokens.uiH3, color: colors.obsidian },
        h4: { ...tokens.uiH3, color: colors.obsidian },
        h5: { ...tokens.uiH3, color: colors.obsidian },
        subtitle: { ...tokens.bodyLarge, color: colors.obsidian, opacity: 0.6 },
        bodyLarge: { ...tokens.bodyLarge, color: colors.textSecondary },
        body: { ...tokens.body, color: colors.textSecondary },
        bodySmall: { ...tokens.body, color: colors.gray },
        caption: { ...tokens.caption, color: colors.gray },
        micro: { ...tokens.caption, color: colors.gray },
        overline: { ...tokens.label, color: colors.solar },
        button: tokens.buttonPrimary,
    } : {}),
};

export type TypographyKey = keyof typeof typography;
export type FontKey = keyof typeof fonts;
